import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";

const ToolModal = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  // Support both ADD_TOOL and EDIT_TOOL types
  const isAddMode = modal?.type === "ADD_TOOL";
  const isEditMode = modal?.type === "EDIT_TOOL";
  const isOpen = (isAddMode || isEditMode) && modal?.isOpen;
  const toolData = modal?.data; // Only available in edit mode

  const [formData, setFormData] = useState({
    name: "",
    function: "",
    category: "",
    status: true,
  });

  const [loading, setLoading] = useState(false);

  // Populate form data in edit mode
  useEffect(() => {
    if (toolData && isEditMode && isOpen) {
      setFormData({
        name: toolData.name || "",
        function: toolData.function || "",
        category: toolData.category || "",
        status: toolData.status === true,
      });
    } else if (isAddMode && isOpen) {
      // Reset form for add mode
      setFormData({
        name: "",
        function: "",
        category: "",
        status: true,
      });
    }
  }, [toolData, isEditMode, isAddMode, isOpen]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Tool name is required");
      return;
    }

    if (!formData.function.trim()) {
      toast.error("Tool function description is required");
      return;
    }

    if (isEditMode && !toolData?._id) {
      toast.error("Invalid tool ID");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        function: formData.function.trim(),
        category: formData.category || "",
        status: formData.status,
      };

      let res;
      if (isEditMode) {
        // Update existing tool
        res = await apiClient.put(`/tools/${toolData._id}`, payload);
      } else {
        // Create new tool
        res = await apiClient.post("/tools", payload);
      }

      if (res?.status) {
        toast.success(
          res?.message ||
          (isEditMode ? "Tool updated successfully" : "Tool created successfully")
        );

        // Dispatch update notification to trigger Tools component refresh
        dispatch(
          closeModalWithUpdate({
            type: null,
            isOpen: false,
            isUpdated: {
              isUpdated: true,
              isUpdatedIn: "TOOLS",
              data: res.data,
              timestamp: Date.now(),
            },
          })
        );

        // Reset form data
        setFormData({
          name: "",
          function: "",
          category: "",
          status: true,
        });

        // Don't call handleClose() here as it would interfere with the update notification
        return;
      } else {
        toast.error(
          res?.message ||
          (isEditMode ? "Failed to update tool" : "Failed to create tool")
        );
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} tool:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Error ${isEditMode ? "updating" : "creating"} tool`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!toolData?._id) {
      toast.error("Invalid tool ID");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${toolData?.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.delete(`/tools/${toolData._id}`);

      if (res?.status) {
        toast.success(res?.message || "Tool deleted successfully");

        // Dispatch update notification to trigger Tools component refresh
        dispatch(
          closeModalWithUpdate({
            type: null,
            isOpen: false,
            isUpdated: {
              isUpdated: true,
              isUpdatedIn: "TOOLS",
              data: null,
              timestamp: Date.now(),
            },
          })
        );

        // Reset form data
        setFormData({
          name: "",
          function: "",
          category: "",
          status: true,
        });

        // Don't call handleClose() here as it would interfere with the update notification
        return;
      } else {
        toast.error(res?.message || "Failed to delete tool");
      }
    } catch (error) {
      console.error("Error deleting tool:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error deleting tool";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      function: "",
      category: "",
      status: true,
    });
    dispatch(closeModal());
  };

  const CATEGORIES = [
    "Scheduling",
    "Communication",
    "Data",
    "Automation",
    "Analytics",
    "Other",
  ];

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEditMode ? "Edit Tool" : "Add New Tool"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Info Alert */}
          {isAddMode && (
            <Alert severity="info">
              Create a new tool that can be used by your AI assistants.
            </Alert>
          )}

          {/* Tool ID Info (Edit mode only) */}
          {isEditMode && toolData?._id && (
            <Alert severity="info" icon={false}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary">
                  Tool ID:
                </Typography>
                <Chip
                  label={toolData._id}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", fontSize: 10 }}
                />
              </Box>
            </Alert>
          )}

          {/* Tool Information */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tool Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Tool Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                placeholder="e.g., Call-end, Summarizer"
              />
              <TextField
                fullWidth
                label="Function Description"
                value={formData.function}
                onChange={(e) => handleChange("function", e.target.value)}
                required
                multiline
                rows={3}
                placeholder="Describe what this tool does..."
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleChange("category", e.target.value)}
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider />

          {/* Status */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tool Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) => handleChange("status", e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {formData.status ? "Active" : "Inactive"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.status
                      ? "This tool is active and can be used"
                      : "This tool is inactive and cannot be used"}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Created Date (Edit mode only) */}
          {isEditMode && toolData?.created_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created: {new Date(toolData.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {/* Delete Button (Edit mode only) */}
        {isEditMode && (
          <CustomButton
            icon={<DeleteIcon />}
            iconPosition="left"
            text="Delete"
            variant="outlined"
            onClick={handleDelete}
            disabled={loading}
            sx={{
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                borderColor: "error.dark",
                backgroundColor: "error.light",
              },
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <CustomButton
          text="Cancel"
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
        />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={
            loading
              ? (isEditMode ? "Updating..." : "Creating...")
              : (isEditMode ? "Update Tool" : "Create Tool")
          }
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.function.trim() || loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ToolModal;
